import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../api';
import './SuperuserDbSnapshot.css';

const TABLE_LABELS = {
  calendar_event: 'CalendarEvent',
  agent_task: 'AgentTask',
  financial_rule: 'FinancialRule',
  financial_payment: 'FinancialPayment',
  user_telegram_chat: 'UserTelegramChat',
  reminder_rule: 'ReminderRule (по пользователю)',
  chat_message: 'ChatMessage',
  message_attachment: 'MessageAttachment',
};

const GLOBAL_LABELS = {
  reminder_rule: 'ReminderRule (глобальные, user=NULL)',
  agent_execution_step: 'AgentExecutionStep',
  agent_execution_run: 'AgentExecutionRun',
};

export default function SuperuserDbSnapshot() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [openTable, setOpenTable] = useState(null);
  const [clearBusy, setClearBusy] = useState(false);
  const [ageBusy, setAgeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/db-snapshot/?limit=500');
      if (res.status === 403) {
        setError('Нет доступа (нужен суперпользователь).');
        setData(null);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Ошибка ${res.status}`);
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (data?.users?.length && selectedUserId == null) {
      setSelectedUserId(data.users[0].id);
    }
  }, [data, selectedUserId]);

  const selected = data?.users?.find((u) => u.id === selectedUserId) ?? null;

  const toggleTable = (key) => {
    setOpenTable((prev) => (prev === key ? null : key));
  };

  const clearSelectedUserChatHistory = useCallback(async () => {
    if (!selected) return;
    const confirmed = window.confirm(
      `Удалить всю историю сообщений пользователя ${selected.username} (id=${selected.id})? Это действие необратимо.`
    );
    if (!confirmed) return;

    setClearBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/clear-user-chat-history/', {
        method: 'POST',
        body: JSON.stringify({ user_id: selected.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Ошибка ${res.status}`);
      }
      await load();
      alert(
        `История очищена: удалено сообщений ${body.deleted_messages ?? 0}, вложений ${body.deleted_attachments ?? 0}.`
      );
    } catch (e) {
      setError(e.message || 'Ошибка очистки истории');
    } finally {
      setClearBusy(false);
    }
  }, [load, selected]);

  const ageSelectedUserChatMessages = useCallback(async () => {
    if (!selected) return;
    const confirmed = window.confirm(
      `Сдвинуть время всех сообщений и вложений пользователя ${selected.username} (id=${selected.id}) на 15 минут назад?`
    );
    if (!confirmed) return;

    setAgeBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/age-user-chat-messages/', {
        method: 'POST',
        body: JSON.stringify({ user_id: selected.id, minutes: 15 }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Ошибка ${res.status}`);
      }
      await load();
      alert(
        `Время сдвинуто: обновлено сообщений ${body.updated_messages ?? 0}, вложений ${body.updated_attachments ?? 0}.`
      );
    } catch (e) {
      setError(e.message || 'Ошибка сдвига времени');
    } finally {
      setAgeBusy(false);
    }
  }, [load, selected]);

  if (loading && !data) {
    return (
      <div className="db-snapshot db-snapshot--loading">Загрузка таблиц…</div>
    );
  }

  if (error && !data) {
    return (
      <div className="db-snapshot db-snapshot--error">
        <p>{error}</p>
        <button type="button" onClick={load} className="db-snapshot-retry">
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div className="db-snapshot">
      <div className="db-snapshot-header">
        <h2 className="db-snapshot-title">Таблицы БД по пользователям</h2>
        <div className="db-snapshot-actions">
          <span className="db-snapshot-meta">
            До {data?.limit_per_table ?? '—'} строк на таблицу в блоке пользователя
          </span>
          <button type="button" onClick={load} className="db-snapshot-refresh" disabled={loading}>
            {loading ? 'Обновление…' : 'Обновить'}
          </button>
        </div>
      </div>

      {error && (
        <div className="db-snapshot-banner">{error}</div>
      )}

      <div className="db-snapshot-userbar">
        <label htmlFor="db-snapshot-user">Пользователь:</label>
        <select
          id="db-snapshot-user"
          className="db-snapshot-select"
          value={selectedUserId ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            setSelectedUserId(v ? Number(v) : null);
            setOpenTable(null);
          }}
        >
          {data?.users?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.username} (id={u.id}
              {u.is_superuser ? ', superuser' : ''}
              {!u.is_active ? ', неактивен' : ''})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="db-snapshot-clear-history"
          onClick={clearSelectedUserChatHistory}
          disabled={!selected || clearBusy || ageBusy || loading}
          title="Удалить ChatMessage и MessageAttachment выбранного пользователя"
        >
          {clearBusy ? 'Очистка…' : 'Очистить историю сообщений'}
        </button>
        <button
          type="button"
          className="db-snapshot-age-messages"
          onClick={ageSelectedUserChatMessages}
          disabled={!selected || clearBusy || ageBusy || loading}
          title="Сдвинуть created_at всех сообщений и вложений на 15 минут назад"
        >
          {ageBusy ? 'Сдвиг…' : '−15 мин к времени сообщений'}
        </button>
      </div>

      {selected && (
        <div className="db-snapshot-sections">
          {Object.entries(selected.tables).map(([tableKey, rows]) => {
            const label = TABLE_LABELS[tableKey] || tableKey;
            const isOpen = openTable === `u:${tableKey}`;
            return (
              <section key={tableKey} className="db-snapshot-table-block">
                <button
                  type="button"
                  className="db-snapshot-table-toggle"
                  onClick={() => toggleTable(`u:${tableKey}`)}
                  aria-expanded={isOpen}
                >
                  <span className="db-snapshot-table-name">{label}</span>
                  <span className="db-snapshot-table-count">{rows.length} строк</span>
                  <span className="db-snapshot-chevron">{isOpen ? '▼' : '▶'}</span>
                </button>
                {isOpen && (
                  <div className="db-snapshot-table-wrap">
                    {rows.length === 0 ? (
                      <p className="db-snapshot-empty">Нет строк</p>
                    ) : (
                      <RawRowsTable rows={rows} />
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <h3 className="db-snapshot-global-title">Без привязки к пользователю</h3>
      <div className="db-snapshot-sections">
        {data?.global &&
          Object.entries(data.global).map(([tableKey, rows]) => {
            const label = GLOBAL_LABELS[tableKey] || tableKey;
            const isOpen = openTable === `g:${tableKey}`;
            return (
              <section key={tableKey} className="db-snapshot-table-block db-snapshot-table-block--global">
                <button
                  type="button"
                  className="db-snapshot-table-toggle"
                  onClick={() => toggleTable(`g:${tableKey}`)}
                  aria-expanded={isOpen}
                >
                  <span className="db-snapshot-table-name">{label}</span>
                  <span className="db-snapshot-table-count">{rows.length} строк</span>
                  <span className="db-snapshot-chevron">{isOpen ? '▼' : '▶'}</span>
                </button>
                {isOpen && (
                  <div className="db-snapshot-table-wrap">
                    {rows.length === 0 ? (
                      <p className="db-snapshot-empty">Нет строк</p>
                    ) : (
                      <RawRowsTable rows={rows} />
                    )}
                  </div>
                )}
              </section>
            );
          })}
      </div>
    </div>
  );
}

function RawRowsTable({ rows }) {
  const keys = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => Object.keys(r).forEach((k) => set.add(k)));
    return Array.from(set).sort();
  }, [rows]);

  return (
    <div className="db-snapshot-scroll">
      <table className="db-snapshot-data-table">
        <thead>
          <tr>
            {keys.map((k) => (
              <th key={k}>{k}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {keys.map((k) => (
                <td key={k}>
                  <CellValue value={row[k]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CellValue({ value }) {
  if (value === null || value === undefined) return <span className="db-null">null</span>;
  if (typeof value === 'object') {
    return (
      <pre className="db-json">{JSON.stringify(value, null, 0)}</pre>
    );
  }
  const s = String(value);
  if (s.length > 200) {
    return <span title={s}>{s.slice(0, 200)}…</span>;
  }
  return s;
}
